import { useTranslation } from "react-i18next";
import { Box, TableRow, TableHead, TableBody, Table, Typography, TableCell, Chip, LinearProgress } from "@mui/material";

import { GinoFitScoreData } from "../../types/candidate";

const CandidateSummary = ({ ginoFitScoreData }: { ginoFitScoreData: GinoFitScoreData }) => {
  const { t } = useTranslation("goHire", { keyPrefix: "candidateGinoFitScorePage" });
  const { t: tCompliance } = useTranslation("goHire", { keyPrefix: "enum.compliance" });

  const tableColumns = [
    { label: t("required"), value: "required" },
    { label: t("jobRequirement"), value: "jobRequirement" },
    { label: t("candidateSubmitted"), value: "candidateSubmitted" },
    { label: t("compliance"), value: "compliance" },
    { label: t("fitScore"), value: "fitScore" },
  ];

  return (
    <Box sx={styles.container}>
      <Box sx={styles.sectionHeader}>
        <Typography sx={styles.sectionTitle}>{t("requiredSubmission")}</Typography>
      </Box>
      <Box sx={styles.tableContainerWrapper}>
        <Table sx={styles.tableContainer}>
          <TableHead>
            <TableRow>
              {tableColumns.map(({ label, value }) => (
                <TableCell key={value}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {ginoFitScoreData.requiredSubmission.map(
              ({ title, jobRequirement, candidateSubmitted, compliance, fitScore }, idx) => {
                const isJobRequirementMultipleValues = Array.isArray(jobRequirement);
                const isCandidateSubmittedMultipleValues = Array.isArray(candidateSubmitted);

                const isJobRequirementBoolean = typeof jobRequirement === "boolean";
                const isCandidateSubmittedBoolean = typeof candidateSubmitted === "boolean";

                const jobRequirementLength = isJobRequirementMultipleValues ? jobRequirement.length : 0;
                const candidateSubmittedLength = isCandidateSubmittedMultipleValues ? candidateSubmitted.length : 0;
                return (
                  <TableRow key={idx}>
                    <TableCell sx={styles.tableRowTitleCell}>{title}</TableCell>
                    <TableCell>
                      {isJobRequirementMultipleValues ? (
                        <Box sx={styles.tableRowCellMultipleValues}>
                          {jobRequirementLength > 2 && (
                            <>
                              <Chip title={jobRequirement[0]} label={jobRequirement[0]} sx={styles.tableCellChip} />
                              <Chip
                                title={jobRequirement.slice(1).join(", ")}
                                label={`+${jobRequirementLength - 1}`}
                                sx={styles.tableCellChip}
                              />
                            </>
                          )}
                          {jobRequirementLength <= 2 &&
                            jobRequirement.map((item) => (
                              <Chip
                                title={item}
                                label={item}
                                sx={{ ...styles.tableCellChip, ...styles.chipEllipsis }}
                              />
                            ))}
                        </Box>
                      ) : isJobRequirementBoolean ? (
                        Boolean(jobRequirement) ? (
                          <Chip label={t("yes")} sx={styles.tableCellChip} />
                        ) : (
                          <Chip label={t("no")} sx={styles.tableCellChip} />
                        )
                      ) : (
                        jobRequirement
                      )}
                    </TableCell>
                    <TableCell>
                      {isCandidateSubmittedMultipleValues ? (
                        <Box sx={styles.tableRowCellMultipleValues}>
                          {candidateSubmittedLength > 2 && (
                            <>
                              <Chip
                                title={candidateSubmitted[0]}
                                label={candidateSubmitted[0]}
                                sx={styles.tableCellChip}
                              />
                              <Chip
                                title={candidateSubmitted.slice(1).join(", ")}
                                label={`+${candidateSubmittedLength - 1}`}
                                sx={styles.tableCellChip}
                              />
                            </>
                          )}
                          {candidateSubmittedLength <= 2 &&
                            candidateSubmitted.map((item) => (
                              <Chip
                                title={item}
                                label={item}
                                sx={{ ...styles.tableCellChip, ...styles.chipEllipsis }}
                              />
                            ))}
                        </Box>
                      ) : isCandidateSubmittedBoolean ? (
                        Boolean(candidateSubmitted) ? (
                          <Chip label={t("yes")} sx={styles.tableCellChip} />
                        ) : (
                          <Chip label={t("no")} sx={styles.tableCellChip} />
                        )
                      ) : (
                        candidateSubmitted
                      )}
                    </TableCell>
                    <TableCell>{tCompliance(compliance)}</TableCell>
                    <TableCell>
                      <Box sx={styles.fitScoreContainer}>
                        <LinearProgress value={fitScore} variant="determinate" sx={styles.fitScoreProgress(fitScore)} />
                        <Typography sx={styles.fitScorePercentage}>{fitScore}%</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              }
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default CandidateSummary;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
  },
  sectionHeader: {
    padding: "20px 16px 26px 16px",
  },
  sectionTitle: {
    fontFamily: "Montserrat",
    color: "var(--Gray-900, #101828)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  tableContainerWrapper: {
    overflowX: "auto",
  },
  tableContainer: {
    width: "100%",
    "& .MuiTableHead-root": {
      backgroundColor: "var(--Gray-50, #F9FAFB)",
    },
    "& .MuiTableCell-head": {
      borderTop: "1px solid var(--Gray-200, #EAECF0)",
      color: "var(--Gray-600, #475467)",
      fontFamily: "Montserrat",
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "18px",
      padding: "12px 20px",
      textWrap: "nowrap",
    },
    "& .MuiTableCell-root": {
      textWrap: "nowrap",
    },
    "& .MuiTableCell-body": {
      borderBottom: "1px solid var(--Gray-200, #EAECF0)",
      padding: "16px 20px",
    },
    "& .MuiTableRow-root:last-child .MuiTableCell-body": {
      borderBottom: "none",
    },
  },
  tableRowTitleCell: {
    textWrap: "nowrap",
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  tableRowCellMultipleValues: {
    display: "flex",
    gap: "4px",
  },
  tableCellChip: {
    height: "fit-content",
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",

    padding: "2px 8px",

    borderRadius: "16px",
    border: "1px solid var(--Gray-200, #E4E7EC)",
    background: "var(--Gray-50, #F9FAFB)",

    "& .MuiChip-label": {
      padding: "0",
    },
  },
  chipEllipsis: {
    maxWidth: "65px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fitScoreContainer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  fitScoreProgress: (fitScore: number) => ({
    width: "40px",
    height: "8px",
    borderRadius: "4px",
    backgroundColor: "#E4E7EC",
    "& .MuiLinearProgress-bar": {
      background:
        fitScore > 60
          ? "var(--Success-500, #12B76A)"
          : fitScore > 30
          ? "var(--Yellow---500, #DC6803)"
          : "var(--Red---500, #B42318)",
      borderRadius: "0",
    },
  }),
  fitScorePercentage: {
    fontFamily: "Montserrat",
    color: "var(--Gray-700, #344054)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
};
